<template>
  <div>
    <div class="blocks">
      <div class="pt-3 l-m-b-20 align-items-center d-flex justify-content-between">
        <div class="d-flex align-items-center">
          <h1 class="pr-3">Users</h1>
          <div class="input-group search">
            <button type="button" class="btn input-group-btn" @click="clearSearch">
              <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
            </button>
            <input class="form-control" type="text" v-model="q" @keyup="searchText($event)"
                   placeholder="Search"
            />
          </div>
        </div>
        <div class="text-right"
             v-if="$_can('clusters.users.store')"
        >
          <button class="btn btn-primary" @click="showCreateSidebar = !showCreateSidebar">
            ADD USER
          </button>
        </div>
      </div>
      <div class="row">
        <div class="col-11 filters-field">
          <span>Filters:</span>
          <span v-if="filtersLoaded">
            <span v-if="filters.carrier.length && carriers && carriers.length">
              <span v-for="carrier in filters.carrier" :key="carrier" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('carrier', carrier)" />
                Carrier: {{ carriers.find(el => el.id === carrier).name }}</span>
            </span>
            <span v-if="filters.company.length && companies && companies.length">
              <span v-for="company in filters.company" :key="company" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('company', company)" />
                Company: {{ companies.find(el => el.id === company).name }}</span>
            </span>
            <span v-if="filters.role.length && roles && roles.length">
              <span v-for="role in filters.role" :key="role" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('role', role)" />
                Role: {{ roles.find(el => el.id === role).name }}</span>
            </span>
            <span v-if="filters.status && userStatuses && userStatuses.length > 0" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('status')" />
              Status: {{ userStatuses.find(el => el.id === filters.status).name }}</span>
          </span>
          <button class="btn btn-secondary add-filters" @click="showFilters = true">
            <i class="fa fa-plus filter-icon" />
          </button>
        </div>
        <div class="col-1 d-flex justify-content-end">
          <button class="btn btn-secondary expand-all" @click="toggleAllUsers">
            <i :class="[users && expandedAll ? 'fa fa-compress' : 'fa fa-expand']" />
          </button>
        </div>
      </div>
    </div>
    <template v-if="!users">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <div class="blocks">
        <UsersBlock
          v-for="(user, index) in users" :key="index"
          :user.sync="user"
          :expanded="expanded"
          :q="q"
          @expanded="toggleUser"
          @update="getUsers"
          @setFiltersSaved="callSetFiltersSaved"
        />
      </div>

      <div class="row blocks">
        <div class="col-sm-6 mb-1 text-black-50 pl-0"
             v-if="meta.total"
        >
          Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
        </div>
        <pagination class="col-sm-6 mb-1 pr-0" :change-page="changePage" :meta="meta" />
      </div>
    </template>

    <!-- Filters -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showFilters" @click.self="showFilters = false">
        <div v-if="showFilters" class="filters-side-modal flex flex-column">
          <div @click="showFilters = !showFilters" class="pointer">
            <h1 class="modal-h1 l-m-b-20"><span class="btn btn-back" />Add Filter</h1>
          </div>
          <div>
            <p>
              Filters are applied automatically. You can remove them one by one with minus button or with clear filters button.
            </p>
          </div>
          <div class="l-bg-white">
            <div class="row">
              <div class="col">
                <div class="row filters">
                  <div class="col-12" v-if="companies && companies.length">
                    <div class="select-wrap">
                      <label>Company</label>
                      <key-multiselect v-model="filters.company"
                                       :options="companies"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :close-on-select="false"
                                       :hide-selected="true"
                                       :id="'company-select'"
                                       :placeholder="'Select Company'"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12" v-if="carriers && carriers.length">
                    <div class="select-wrap">
                      <label>Carriers</label>
                      <key-multiselect v-model="filters.carrier"
                                       :options="carriers"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :close-on-select="false"
                                       :hide-selected="true"
                                       :id="'carrier-select'"
                                       :placeholder="'Select Carriers'"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12" v-if="roles && roles.length">
                    <div class="select-wrap">
                      <label>Role</label>
                      <key-multiselect v-model="filters.role"
                                       :options="roles"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :close-on-select="false"
                                       :hide-selected="true"
                                       :id="'role-select'"
                                       :placeholder="'Select Role'"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Status</label>
                      <simple-select v-model="filters.status"
                                     :options="userStatuses"
                                     placeholder="Select Status"
                                     @input="search"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-auto">
            <div class="col">
              <button type="button" class="btn btn-not-active text-dark w-100" @click="clearFilters"
                      v-if="!clearFiltersButtonShouldBeDisabled"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Create User -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showCreateSidebar" @click.self="showCreateSidebar = false">
        <users-create class="sidebar-right" @cancel="showCreateSidebar = false"
                      @update="updateAfterCreating"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import Pagination from '../Pagination';
import PhoneMask from '../../classes/PhoneMask';
import UsersBlock from './UsersBlock';
import UsersCreate from './UsersCreate';

export default {
  name: 'Users',
  components: {
    UsersBlock,
    Pagination,
    UsersCreate,
  },
  data: () => ({
    expanded: [],
    expandedAll: false,
    filters: {
      company: [],
      carrier: [],
      role: [],
      status: '',
    },
    page: 1,
    q: '',
    showCreateSidebar: false,
    phoneMask: new PhoneMask(),
    showFilters: false,
  }),
  computed: {
    ...mapState({
      users: state => state.users.users.data,
      user: state => state.auth.dispatchUser,
      meta: state => state.users.users.meta,
      carriers: state => state.carriers.carriers.data,
      companies: state => state.companies.companies.data,
      roles: state => state.roles.roles,
      userStatuses: state => state.values.userStatuses,
      filtersSaved: state => state.filters.filtersSaved,
      filtersLoaded: state => state.guard.filtersLoaded,
    }),
    ...mapGetters(['getUsersFiltered', 'getFiltersSaved']),
    clearFiltersButtonShouldBeDisabled() {
      return (
        !this.filters.carrier.length &&
        !this.filters.company.length &&
        !this.filters.role.length &&
        !this.filters.status
      );
    },
  },
  watch: {
    users() {
      if (this.expandedAll) {
        this.users.forEach(user => {
          this.expanded.push(user.id);
        });
      } else {
        this.expanded = [];
      }
    },
  },
  mounted() {
    if (this.filtersSaved && this.filtersSaved.previousPage === 'UsersEdit') {
      this.filters = { ...this.filtersSaved };
      this.page = this.filtersSaved.page;
      this.filter();
    } else {
      if (Object.keys(this.$route.query).length !== 0) {
        this.setFilters(this.$route.query);
      }
      this.filter();
    }
  },
  methods: {
    ...mapActions(['getUsers', 'getClusterUsers']),
    ...mapMutations(['setUsers', 'setFiltersSaved']),
    setFilters(query) {
      this.filters = Object.assign(
        {
          carrier: query.carrier_id ? query.carrier_id.split(',').map(i => Number(i)) : [],
          company: query.company_id ? query.company_id.split(',').map(i => Number(i)) : [],
          role: query.role_id ? query.role_id.split(',').map(i => Number(i)) : [],
          status: query.user_status_id ? parseInt(query.user_status_id, 10) : '',
        },
        {},
      );
      this.page = query.page;
      this.q = query.q ? query.q.toString() : '';
    },
    callSetFiltersSaved() {
      this.setFiltersSaved({ ...this.filters, previousPage: 'UsersEdit', page: this.page });
    },
    view(event, id) {
      if (event.button === 1) {
        window.open(this.$router.resolve(`/users/edit/${id}`).href, '_blank');
      } else {
        this.$router.push(`/users/edit/${id}`);
      }
    },
    changePage(pageNum) {
      this.setUsers({});
      this.page = pageNum;
      this.filter();
    },
    searchText(e) {
      this.page = 1;
      if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
        const vm = this;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          vm.filter();
        }, 500);
      }
    },
    search() {
      this.page = 1;
      this.filter();
    },
    filter() {
      const query = `page=${this.page}&carrier_id=${this.filters.carrier}&company_id=${
        this.filters.company
      }&role_id=${this.filters.role}&user_status_id=${this.filters.status}&q=${this.q}`;
      this.getUsersFiltered(query);
      if (this.$_checkProperties(this.filters)) {
        this.$router.push(`/users?${query}`);
      } else {
        this.$router.push(`/users`);
      }
    },
    clearFilters() {
      this.filters = Object.assign(
        {
          carrier: [],
          company: [],
          role: [],
          status: '',
        },
        {},
      );
      this.filter();
      this.showFilters = false;
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
    removeFilter(model, id) {
      if (id) {
        this.filters[model] = this.filters[model].filter(element => element !== id);
      } else {
        this.filters[model] = '';
      }
      if (model === 'move_date') {
        this.filters.startDate = '';
        this.filters.endDate = '';
      }
      this.search();
    },
    updateAfterCreating() {
      this.getUsers();
      this.getClusterUsers();
    },
    toggleUser(value) {
      if (this.expanded.includes(value)) {
        const index = this.expanded.indexOf(value);
        if (index > -1) {
          this.expanded.splice(index, 1);
        }
      } else {
        this.expanded.push(value);
      }
    },
    toggleAllUsers() {
      if (!this.expanded.length) {
        this.users.forEach(user => {
          this.expanded.push(user.id);
          this.expandedAll = true;
        });
      } else {
        this.expanded = [];
        this.expandedAll = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/users/Users.vue